import { useMemo } from 'react';
import { Book } from '@/types/book';
import { useBooks } from './use-books';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useProfileHierarchy } from './use-profile-hierarchy';

export interface EpisodeWithProgress extends Book {
  firstPageImage?: string | null;
  progress?: number; // 0-100
  isWatched?: boolean;
}

export interface SeriesInfo {
  isPartOfSeries: boolean;
  seriesId: string | null;
  seriesTitle: string | null;
  episodes: EpisodeWithProgress[];
  currentEpisodeNumber: number | null;
  nextEpisode: EpisodeWithProgress | null;
  previousEpisode: EpisodeWithProgress | null;
  totalEpisodes: number;
}

export const useSeriesEpisodes = (book: Book | null): SeriesInfo => {
  const { allBooks } = useBooks();
  const { activeProfile } = useProfileHierarchy();

  // Get series episodes
  const seriesEpisodes = useMemo(() => {
    if (!book?.series_id) return [];
    return allBooks
      .filter(b => b.series_id === book.series_id)
      .sort((a, b) => (a.episode_number || 0) - (b.episode_number || 0));
  }, [book?.series_id, allBooks]);

  // Fetch page_images for all episodes in series
  const { data: episodePageImages } = useQuery({
    queryKey: ['series-page-images', book?.series_id],
    queryFn: async () => {
      if (!book?.series_id || seriesEpisodes.length === 0) return {};
      const episodeIds = seriesEpisodes.map(ep => ep.id);
      const { data, error } = await supabase
        .from('books')
        .select('id, page_images, cover_image_url')
        .in('id', episodeIds);
      
      if (error) return {};
      
      const imageMap: Record<number, string | null> = {};
      data?.forEach(item => {
        const pageImages = item.page_images as Array<{ pageName: string; url: string }> | null;
        if (pageImages && Array.isArray(pageImages)) {
          // Find page-1 image for landscape thumbnail
          const page1 = pageImages.find(p => p.pageName === 'page-1');
          imageMap[item.id] = page1?.url || item.cover_image_url || null;
        } else {
          imageMap[item.id] = item.cover_image_url || null;
        }
      });
      return imageMap;
    },
    enabled: !!book?.series_id && seriesEpisodes.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch reading progress for all episodes from reading_sessions
  const { data: progressData } = useQuery({
    queryKey: ['series-progress', book?.series_id, activeProfile?.id],
    queryFn: async () => {
      if (!book?.series_id || !activeProfile?.id || seriesEpisodes.length === 0) return {};
      const episodeIds = seriesEpisodes.map(ep => ep.id);
      
      // Get the latest session for each book to determine progress
      const { data, error } = await supabase
        .from('reading_sessions')
        .select('book_id, current_page')
        .eq('profile_id', activeProfile.id)
        .in('book_id', episodeIds)
        .order('created_at', { ascending: false });
      
      if (error) return {};
      
      // Get unique latest progress per book
      const progressMap: Record<number, { progress: number; isWatched: boolean }> = {};
      const seen = new Set<number>();
      
      data?.forEach(item => {
        if (!seen.has(item.book_id)) {
          seen.add(item.book_id);
          // Estimate progress - assume ~20 pages per book if we don't have total
          const estimatedTotal = 20;
          const progress = Math.min(100, Math.round((item.current_page / estimatedTotal) * 100));
          progressMap[item.book_id] = {
            progress: Math.min(progress, 100),
            isWatched: item.current_page >= 15, // Consider "watched" if reached page 15+
          };
        }
      });
      return progressMap;
    },
    enabled: !!book?.series_id && !!activeProfile?.id && seriesEpisodes.length > 0,
    staleTime: 1 * 60 * 1000,
  });

  return useMemo(() => {
    const defaultResult: SeriesInfo = {
      isPartOfSeries: false,
      seriesId: null,
      seriesTitle: null,
      episodes: [],
      currentEpisodeNumber: null,
      nextEpisode: null,
      previousEpisode: null,
      totalEpisodes: 0,
    };

    if (!book?.series_id || seriesEpisodes.length <= 1) {
      return defaultResult;
    }

    // Enrich episodes with page images and progress
    const enrichedEpisodes: EpisodeWithProgress[] = seriesEpisodes.map(ep => ({
      ...ep,
      firstPageImage: episodePageImages?.[ep.id] || null,
      progress: progressData?.[ep.id]?.progress || 0,
      isWatched: progressData?.[ep.id]?.isWatched || false,
    }));

    const currentIndex = enrichedEpisodes.findIndex(ep => ep.id === book.id);
    const nextEpisode = currentIndex < enrichedEpisodes.length - 1 
      ? enrichedEpisodes[currentIndex + 1] 
      : null;
    const previousEpisode = currentIndex > 0 
      ? enrichedEpisodes[currentIndex - 1] 
      : null;

    return {
      isPartOfSeries: true,
      seriesId: book.series_id,
      seriesTitle: book.series_title || null,
      episodes: enrichedEpisodes,
      currentEpisodeNumber: book.episode_number || null,
      nextEpisode,
      previousEpisode,
      totalEpisodes: enrichedEpisodes.length,
    };
  }, [book, seriesEpisodes, episodePageImages, progressData]);
};
